/**
 * Fetch Cache Utility
 * 
 * Prevents duplicate API calls caused by React StrictMode's double-invoke behavior.
 * Uses sessionStorage to persist cache across React unmount/remount cycles.
 * 
 * The cache tracks "in-flight" requests to prevent duplicates when StrictMode
 * unmounts and remounts components rapidly.
 */

// Use sessionStorage for persistence across StrictMode remounts
const CACHE_PREFIX = 'fetch_cache_';
const CACHE_TIMESTAMP_PREFIX = 'fetch_cache_ts_';
const CACHE_TTL = 2000; // 2 seconds TTL - short enough to allow real navigation

/**
 * Check if a fetch should proceed for the given key
 * Returns false if:
 * - A fetch was completed within the TTL window
 * @param {string} key - Unique identifier for this fetch operation
 * @returns {boolean} - True if fetch should proceed, false if already fetched
 */
export const shouldFetch = (key) => {
  try {
    const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_PREFIX + key);
    
    if (timestamp) {
      const elapsed = Date.now() - parseInt(timestamp, 10);
      // If within TTL, skip fetch
      if (elapsed < CACHE_TTL) {
        return false;
      }
    }
    return true;
  } catch (e) {
    // sessionStorage not available (e.g., incognito mode in some browsers)
    return true;
  }
};

/**
 * Mark a fetch as started/completed for the given key
 * Call this IMMEDIATELY at the start of your fetch (before the actual API call)
 * to prevent StrictMode double-invoke from triggering duplicate requests
 * @param {string} key - Unique identifier for this fetch operation
 */
export const markFetched = (key) => {
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, 'true');
    sessionStorage.setItem(CACHE_TIMESTAMP_PREFIX + key, Date.now().toString());
  } catch (e) {
    // sessionStorage not available
  }
};

/**
 * Reset a fetch key (useful when you want to refetch, e.g., after period change)
 * @param {string} key - Unique identifier for this fetch operation
 */
export const resetFetch = (key) => {
  try {
    sessionStorage.removeItem(CACHE_PREFIX + key);
    sessionStorage.removeItem(CACHE_TIMESTAMP_PREFIX + key);
  } catch (e) {
    // sessionStorage not available
  }
};

/**
 * Reset all fetch keys matching a pattern (useful for period changes)
 * @param {string} pattern - Pattern to match (e.g., 'host-dashboard-')
 */
export const resetFetchPattern = (pattern) => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes(pattern)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (e) {
    // sessionStorage not available
  }
};

/**
 * Reset all fetch keys (useful for logout or hard refresh)
 */
export const resetAllFetches = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith(CACHE_PREFIX) || key.startsWith(CACHE_TIMESTAMP_PREFIX))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (e) {
    // sessionStorage not available
  }
};

/**
 * Custom hook for fetch-once pattern
 * @param {string} key - Unique identifier for this fetch operation
 * @returns {Object} - { shouldFetch, markFetched, resetFetch }
 */
export const useFetchOnce = (key) => {
  return {
    shouldFetch: () => shouldFetch(key),
    markFetched: () => markFetched(key),
    resetFetch: () => resetFetch(key)
  };
};

export default {
  shouldFetch,
  markFetched,
  resetFetch,
  resetFetchPattern,
  resetAllFetches,
  useFetchOnce
};
